import React, { useRef, useCallback, useState, useEffect } from 'react';

interface Sentence {
    text: string;
    speaker?: string | null;
    start_time?: number;
    end_time?: number;
}

interface TranscriptPreviewProps {
    sentences: Sentence[];
    affectedIndices?: number[];
    audioUrl?: string | null;
    focusedIndex?: number | null;
    onPlaySentence?: (index: number) => void;
    onStopSentence?: () => void;
    externalPlayingIndex?: number | null;
}

const TranscriptPreview: React.FC<TranscriptPreviewProps> = ({
    sentences,
    affectedIndices = [],
    audioUrl,
    focusedIndex,
    onPlaySentence,
    onStopSentence,
    externalPlayingIndex,
}) => {
    const affectedSet = new Set(affectedIndices);
    // Fallback: standalone <audio> when no external player is wired
    const audioRef = useRef<HTMLAudioElement>(null);
    const [localPlayingIndex, setLocalPlayingIndex] = useState<number | null>(null);
    const stopTimerRef = useRef<number | null>(null);
    const rowRefs = useRef<Map<number, HTMLDivElement>>(new Map());

    const useExternal = !!onPlaySentence;
    const playingIndex = useExternal ? externalPlayingIndex ?? null : localPlayingIndex;

    // Scroll focused row into view when waveform region is clicked
    useEffect(() => {
        if (focusedIndex == null) return;
        const row = rowRefs.current.get(focusedIndex);
        row?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, [focusedIndex]);

    const handlePlay = useCallback((index: number) => {
        // External player path (waveform)
        if (useExternal) {
            if (playingIndex === index) {
                onStopSentence?.();
            } else {
                onPlaySentence!(index);
            }
            return;
        }

        // Fallback: standalone audio
        const sentence = sentences[index];
        if (!audioRef.current || !audioUrl || sentence.start_time == null) return;

        if (stopTimerRef.current) {
            clearTimeout(stopTimerRef.current);
            stopTimerRef.current = null;
        }

        const audio = audioRef.current;

        if (localPlayingIndex === index && !audio.paused) {
            audio.pause();
            setLocalPlayingIndex(null);
            return;
        }

        audio.currentTime = sentence.start_time;
        setLocalPlayingIndex(index);
        audio.play();

        if (sentence.end_time != null) {
            const duration = (sentence.end_time - sentence.start_time) * 1000;
            stopTimerRef.current = window.setTimeout(() => {
                audio.pause();
                setLocalPlayingIndex(null);
                stopTimerRef.current = null;
            }, duration);
        }
    }, [sentences, audioUrl, localPlayingIndex, playingIndex, useExternal, onPlaySentence, onStopSentence]);

    const handleAudioEnded = useCallback(() => {
        setLocalPlayingIndex(null);
        if (stopTimerRef.current) {
            clearTimeout(stopTimerRef.current);
            stopTimerRef.current = null;
        }
    }, []);

    return (
        <div className="ce-transcript">
            {!useExternal && audioUrl && <audio ref={audioRef} src={audioUrl} preload="metadata" onEnded={handleAudioEnded} />}
            {sentences.map((s, i) => (
                <div
                    key={i}
                    ref={(el) => { if (el) rowRefs.current.set(i, el); else rowRefs.current.delete(i); }}
                    className={`ce-transcript__row ${affectedSet.has(i) ? 'ce-transcript__row--affected' : ''}${focusedIndex === i ? ' ce-transcript__row--focused' : ''}`}
                >
                    {audioUrl && s.start_time != null && (
                        <button
                            className={`ce-transcript__play-btn ${playingIndex === i ? 'ce-transcript__play-btn--active' : ''}`}
                            onClick={() => handlePlay(i)}
                            title={`${formatTime(s.start_time)}${s.end_time != null ? ` - ${formatTime(s.end_time)}` : ''}`}
                            type="button"
                        >
                            {playingIndex === i ? (
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                                    <rect x="2" y="1" width="4" height="12" rx="1" />
                                    <rect x="8" y="1" width="4" height="12" rx="1" />
                                </svg>
                            ) : (
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                                    <path d="M3 1.5v11l9-5.5z" />
                                </svg>
                            )}
                        </button>
                    )}
                    <span className="ce-transcript__index">{i + 1}.</span>
                    {s.start_time != null && s.end_time != null && (
                        <span className="ce-transcript__timing">
                            {formatTime(s.start_time)}-{formatTime(s.end_time)}
                        </span>
                    )}
                    {s.speaker && <span className="ce-transcript__speaker">[{s.speaker}]</span>}
                    <span className="ce-transcript__text">{s.text}</span>
                </div>
            ))}
        </div>
    );
};

function formatTime(seconds?: number): string {
    if (seconds == null) return '';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
}

export default TranscriptPreview;
